import {ConsentString} from 'consent-string';
import {getConsentCookie} from '../services/consent';
import {getVendors} from '../vendors';
import {DIALOG_CLOSED} from '../constants';
import {logger} from '../services/logger';

const API = {};
const gdprApplies = true;
const gdprAppliesGlobally = true;
const hasGlobalScope = false;

/**
 * The vendorIds array contains the vendor ids (as identified in the Global Vendor List) for which consent is being requested.
 * If vendorIds is null or empty, the operation will return consent status for all vendors in the vendor list.
 * The callback function will be called with a VendorConsents object as the parameter. If vendorIds is provided and not empty,
 * then VendorConsents.vendorConsents will only included IDs from vendorIds. The callback is called only after consent is obtained from the UI or existing cookies.
 * The consent will be returned false ("No Consent") for any invalid vendorId.
 * The boolean success parameter passed to the callback indicates whether the call to getVendorConsents() was successful.
 * @param {Array} vendorIds
 * @param {Function} vendorIds
 */
API.getVendorConsents = async (vendorIds, callback) => {
  const euconsent = getConsentCookie();
  const VendorConsents = {
    metadata: '',
    gdprApplies,
    hasGlobalScope,
    purposeConsents: {},
    vendorConsents: {}
  };

  if (euconsent) {
    try {
      const GVL = await getVendors();
      const consentData = new ConsentString(euconsent);
      const purposeConsents = {};
      const vendorConsents = {};
      const metadata = consentData.getMetadataString();

      GVL.purposes.forEach(({id}) => {
        purposeConsents[id] = consentData.isPurposeAllowed(id);
      });

      if (vendorIds === null || vendorIds.length === 0) {
        GVL.vendors.forEach(({id}) => {
          vendorConsents[id] = consentData.isVendorAllowed(id);
        });
      } else {
        vendorIds.forEach(id => {
          vendorConsents[id] = consentData.isVendorAllowed(id);
        });
      }

      return callback({...VendorConsents, metadata, purposeConsents, vendorConsents}, true);
    } catch (error) {
      logger(600, 'Could not retrieve vendors');
      return callback(VendorConsents, false);
    }
  }

  const cb = () => {
    API.getVendorConsents(vendorIds, callback);
    window.removeEventListener(DIALOG_CLOSED, cb);
  };

  window.addEventListener(DIALOG_CLOSED, cb);
};

/**
 * If consentStringVersion is provided, then fetch that version if available (else returns null).
 * If consentStringVersion is null, then the latest supported version of the consent string is returned.
 * The callback is called only after consent is obtained from the UI or existing cookies.
 * The boolean success parameter passed to the callback indicates whether the call to getConsentData() was successful.
 * @param {string} vendorIds
 * @param {callback} vendorIds
 */
API.getConsentData = (consentStringVersion, callback) => {
  const euconsent = getConsentCookie();
  const VendorConsentsData = {
    consentData: euconsent,
    gdprApplies,
    hasGlobalScope
  };

  if (euconsent) {
    const consentData = new ConsentString(euconsent);

    if (consentStringVersion && Number(consentData.getVersion()) !== Number(consentStringVersion)) {
      return callback(null, false);
    }

    return callback(VendorConsentsData, true);
  }

  const cb = () => {
    API.getConsentData(consentStringVersion, callback);
    window.removeEventListener(DIALOG_CLOSED, cb);
  };

  window.addEventListener(DIALOG_CLOSED, cb);
};

/**
 * The "ping" command invokes the callback immediately with information about whether the main CMP
 * script has loaded yet and if GDPR has been configured for all users or just EU users.
 * @param {any} ignored
 * @param {Function} vendorIds
 */
API.ping = (ignored, callback) => {
  const PingObject = {
    gdprAppliesGlobally,
    cmpLoaded: true
  };
  callback(PingObject, true);
};

/**
 * Implementation by
 * https://github.com/InteractiveAdvertisingBureau/GDPR-Transparency-and-Consent-Framework/blob/master/CMP%20JS%20API%20v1.1%20Final.md#CMP-JS-API
 */
export default function setPublicAPI() {
  window.__cmp = (command, parameter, callback) => {
    if (API[command]) {
      return API[command](parameter, callback);
    }
  };
}
